import { useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useQueryClient } from "@tanstack/react-query";

import styles from "./SeonCooldownModal.module.css";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import ToggleSwitch from "../../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { useGetSeonCooldown } from "../../../../../queries/user-card/useGetSeonCooldown";
import { usePostClearSeonCooldown } from "../../../../../queries/user-card/usePostClearSeonCooldown";
import { convertToUserTimezone } from "../../../../../utils/dateService";

function SeonCooldownModal({ handleCancel, data }) {
  const queryClient = useQueryClient();
  const [isApiRunning, setIsApiRunning] = useState(false);

  const { data: cooldownData, isLoading } = useGetSeonCooldown(data?.user_id);
  const { mutateAsync: clearCooldown } = usePostClearSeonCooldown();

  const cooldownActive = cooldownData?.cooldown_active ?? false;
  const [toggleValue, setToggleValue] = useState(null);

  const displayValue = toggleValue ?? cooldownActive;
  const hasChanges = toggleValue !== null && toggleValue !== cooldownActive;

  const handleToggleChange = (checked) => {
    setToggleValue(checked.is_active);
  };

  const onSave = async () => {
    if (!hasChanges) return;

    setIsApiRunning(true);
    try {
      const res = await clearCooldown({ user_id: data.user_id });
      await queryClient.invalidateQueries({
        queryKey: ["seonCooldown", data?.user_id],
      });
      setToggleValue(null);
      message.success(res?.message ?? AppStrings.SEON_COOLDOWN_CLEARED);
    } catch (error) {
      message.error(AppStrings.FAILED_TO_CLEAR_2FA_COOLDOWN);
    } finally {
      setIsApiRunning(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderTitle}>
            <h2 className={styles.modalTitle}>
              {AppStrings.TWO_FA_COOLDOWN}
            </h2>
          </div>
          <CloseOutlined onClick={handleCancel} className="close-icon" />
        </div>
        <div className={styles.loaderContainer}>
          <RebetLoader height={"40px"} width={"40px"} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.modalHeader}>
        <div className={styles.modalHeaderTitle}>
          <h2 className={styles.modalTitle}>{AppStrings.TWO_FA_COOLDOWN}</h2>
        </div>
        <CloseOutlined onClick={handleCancel} className="close-icon" />
      </div>

      <div className={styles.modalContent}>
        <div className={styles.modalContentTitle}>
          <span>
            {AppStrings.USER}: {data.username}
          </span>
        </div>

        <div className={styles.transactionInfoItem}>
          <span>{AppStrings.TWO_FA_COOLDOWN_ENABLED}?</span>
          <div className={styles.toggleContainer}>
            <span
              className={`${styles.toggleStatus} ${
                displayValue ? styles.enabled : styles.disabled
              }`}
            >
              {displayValue ? AppStrings.YES : AppStrings.NO}
            </span>
            <ToggleSwitch
              status={displayValue}
              updateEventCode={handleToggleChange}
              disabled={!cooldownActive || isApiRunning}
            />
          </div>
        </div>

        {cooldownActive && cooldownData?.cooldown_until && (
          <div className={styles.cooldownTimestamp}>
            <span>{AppStrings.WHEN_COOLDOWN_ENDS}</span>
            <span>{convertToUserTimezone(cooldownData.cooldown_until)}</span>
          </div>
        )}
      </div>

      <div className="modal-footer">
        <div className="buttons">
          <button
            className="cancel-button"
            onClick={handleCancel}
            disabled={isApiRunning}
          >
            {AppStrings.CLOSE}
          </button>
          <button
            className="save-button"
            onClick={onSave}
            disabled={isApiRunning || !hasChanges}
          >
            {isApiRunning ? (
              <RebetLoader height={"20px"} width={"20px"} />
            ) : (
              AppStrings.SAVE
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SeonCooldownModal;
